import { useCallback, useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  Divider,
  FormControlLabel,
  Grid,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import AddShoppingCartIcon from "@mui/icons-material/AddShoppingCart";
import DeleteIcon from "@mui/icons-material/Delete";
import PersonIcon from "@mui/icons-material/Person";
import PrintIcon from "@mui/icons-material/Print";
import SaveIcon from "@mui/icons-material/Save";
import DynamicGrid from "../components/DynamicTable";
import { useUserContext } from "../hooks/UserContext";
import { useNotification } from "../hooks/NotificationContext";
import { Cliente } from "../models/ClienteModel";
import { LineaVenta } from "../models/LineaVentaModel";
import { Producto } from "../models/ProductoModel";
import { fetchGetClientes } from "../services/clienteService";
import { fetchCrearPedido, fetchDescargarPedidoPdf, fetchUltimosPedidosCliente, PedidoConDetalleDto } from "../services/pedidoService";
import { CurrencyFormatter } from "../utils/CurrencyFormatter";
import { parseDecimalValue } from "../utils/NumberParser";
import LocalSelector from "./components/LocalSelector";
import { isAbortError } from "../services/fetchService";
import { useProductosVenta } from "../hooks/ProductosVentaContext";
import { CatalogoVentaActualizadoDialog } from "../components/CatalogoVentaActualizadoDialog";
import SelectorProductosVenta from "../components/SelectorProductosVenta";

const gridColumnDefinitions = {
  productos: {
    "PRODUCTO": { Header: "Producto", Editable: false },
    "CODIGO": { Header: "Codigo", Editable: false },
    "CONTADO": { Header: "$ Unitario", Editable: false, IsNumber: true },
  },
  clientes: {
    "CLIENTE": { Header: "Cliente", Editable: false },
  },
};

const getToday = () => new Date().toISOString().slice(0, 10);

const getUnitario = (linea: LineaVenta) => {
  const producto = linea.producto as any;
  return parseDecimalValue(producto.CONTADO ?? producto.contado ?? producto.Contado ?? 0);
};

const getClienteId = (cliente: Cliente | undefined) => {
  const row = cliente as any;
  const id = row?.ID ?? row?.Id ?? row?.id ?? row?.IDCLIENTE ?? row?.IdCliente ?? row?.idCliente;
  return Number(id ?? 0);
};

const getClienteNombre = (cliente: Cliente | undefined) => {
  const row = cliente as any;
  return row?.CLIENTE ?? row?.Cliente ?? row?.cliente ?? "";
};

type PedidoParaImprimir = {
  idPedido: number;
  idLocal: number;
  fechaPedido: Date;
  cliente: string;
  telefono: string;
  fechaEntrega: string;
  horaDesde: string;
  horaHasta: string;
  observacion: string;
  infoEntrega: string;
  lineas: LineaVenta[];
};

const escapeHtml = (value: string) =>
  value
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#039;");

const formatDate = (value: Date | string) => {
  const date = value instanceof Date
    ? value
    : new Date(String(value).includes("T") ? value : `${value}T00:00:00`);
  return Number.isNaN(date.getTime()) ? "" : date.toLocaleDateString("es-AR");
};

const formatTime = (value: string) => {
  if (!value) return "";
  const date = new Date(value);
  if (!Number.isNaN(date.getTime())) {
    return date.toLocaleTimeString("es-AR", { hour: "2-digit", minute: "2-digit" });
  }
  return value.length >= 5 ? value.slice(0, 5) : value;
};

const formatNumber = (value: number) =>
  value.toLocaleString("es-AR", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const PedidoWeb = () => {
  const { getUser, user } = useUserContext();
  const { showNotification } = useNotification();
  const userLoggedIn = useMemo(() => user ?? getUser(), [getUser, user]);
  const [idLocal, setIdLocal] = useState<string | undefined>(undefined);
  const [clientes, setClientes] = useState<Cliente[]>([]);
  const [clienteSeleccionado, setClienteSeleccionado] = useState<Cliente | undefined>(undefined);
  const [lineas, setLineas] = useState<LineaVenta[]>([]);
  const [selectDialogOpen, setSelectDialogOpen] = useState(false);
  const [seleccionandoCliente, setSeleccionandoCliente] = useState(false);
  const [seleccionandoProducto, setSeleccionandoProducto] = useState(false);
  const [dialogData, setDialogData] = useState<any[]>([]);
  const { productos, loading: loadingProductos, revision } = useProductosVenta(idLocal);
  const [loadingUltimosPedidos, setLoadingUltimosPedidos] = useState(false);
  const [guardando, setGuardando] = useState(false);
  const [descargandoPdf, setDescargandoPdf] = useState(false);
  const [ultimoPedidoImpresion, setUltimoPedidoImpresion] = useState<PedidoParaImprimir | null>(null);
  const [ultimosPedidos, setUltimosPedidos] = useState<PedidoConDetalleDto[]>([]);
  const [fechaEntrega, setFechaEntrega] = useState(getToday());
  const [horaDesde, setHoraDesde] = useState("09:00");
  const [horaHasta, setHoraHasta] = useState("18:00");
  const [retiraDelLocal, setRetiraDelLocal] = useState(true);
  const [sucursalEntrega, setSucursalEntrega] = useState("");
  const [observacion, setObservacion] = useState("");

  const total = useMemo(
    () => lineas.reduce((acc, linea) => acc + getUnitario(linea) * linea.cantidad, 0),
    [lineas]
  );

  const showMessage = useCallback((message: string, level: "success" | "info" | "warning" | "error") => {
    showNotification(message, level);
  }, [showNotification]);

  useEffect(() => {
    if (userLoggedIn) {
      const controller = new AbortController();
      fetchGetClientes(userLoggedIn, controller.signal)
        .then(setClientes)
        .catch((err) => {
          if (isAbortError(err)) return;
          console.log(err);
          showMessage("Error al cargar clientes", "error");
        });

      return () => controller.abort();
    }
  }, [userLoggedIn, showMessage]);

  const openClienteDialog = () => {
    setSeleccionandoProducto(false);
    setSeleccionandoCliente(true);
    setDialogData(clientes);
    setSelectDialogOpen(true);
  };

  const openProductoDialog = () => {
    if (!idLocal) {
      showMessage("Debe seleccionar un local primero", "warning");
      return;
    }

    if (loadingProductos || productos.length === 0) {
      showMessage("Los productos todavia se estan cargando", "info");
      return;
    }

    setSeleccionandoCliente(false);
    setSeleccionandoProducto(true);
    setSelectDialogOpen(true);
  };

  const cargarUltimosPedidos = useCallback((cliente: Cliente | undefined = clienteSeleccionado) => {
    if (!userLoggedIn || !cliente) {
      setUltimosPedidos([]);
      return Promise.resolve();
    }

    const idCliente = getClienteId(cliente);
    if (!idCliente || Number.isNaN(idCliente)) {
      setUltimosPedidos([]);
      showMessage("No se pudo determinar el id del cliente seleccionado", "warning");
      return Promise.resolve();
    }

    setLoadingUltimosPedidos(true);
    return fetchUltimosPedidosCliente(userLoggedIn, idCliente)
      .then(setUltimosPedidos)
      .catch((err) => {
        console.log(err);
        setUltimosPedidos([]);
        showMessage(err?.message ?? "No se pudieron cargar los ultimos pedidos del cliente", "error");
      })
      .finally(() => setLoadingUltimosPedidos(false));
  }, [clienteSeleccionado, userLoggedIn, showMessage]);

  useEffect(() => {
    if (clienteSeleccionado) {
      cargarUltimosPedidos(clienteSeleccionado);
    } else {
      setUltimosPedidos([]);
    }
  }, [clienteSeleccionado, cargarUltimosPedidos]);

  const handleSelectRow = (params: any) => {
    if (seleccionandoCliente) {
      setClienteSeleccionado(params.row as Cliente);
    }

    setSelectDialogOpen(false);
  };

  const handleAgregarProducto = (producto: Producto) => {
    setLineas((actuales) => {
      const id = producto.IDPRODUCTO ?? producto.idProducto;
      const existente = actuales.findIndex((linea) => (linea.producto.IDPRODUCTO ?? linea.producto.idProducto) === id);
      if (existente < 0) return [...actuales, { producto: { ...producto }, cantidad: 1 }];
      return actuales.map((linea, index) => index === existente ? { ...linea, cantidad: linea.cantidad + 1 } : linea);
    });
  };

  const updateCantidad = (index: number, value: string) => {
    const cantidad = Math.max(parseDecimalValue(value), 0);
    setLineas((actuales) => actuales.map((linea, i) => i === index ? { ...linea, cantidad } : linea));
  };

  const deleteLinea = (index: number) => {
    setLineas((actuales) => actuales.filter((_, i) => i !== index));
  };

  const imprimirPedido = (pedido: PedidoParaImprimir | null = ultimoPedidoImpresion) => {
    if (!pedido) {
      showMessage("No hay un pedido guardado para imprimir", "warning");
      return;
    }

    const totalPedido = pedido.lineas.reduce((acc, linea) => acc + getUnitario(linea) * linea.cantidad, 0);
    const detalleHtml = pedido.lineas.map((linea) => {
      const subtotal = getUnitario(linea) * linea.cantidad;
      return `
        <tr>
          <td class="center">${formatNumber(linea.cantidad)}</td>
          <td>${escapeHtml(linea.producto.PRODUCTO)}</td>
          <td class="right">${formatNumber(subtotal)}</td>
        </tr>`;
    }).join("");

    const printWindow = window.open("", "_blank", "width=700,height=900");
    if (!printWindow) {
      showMessage("El navegador bloqueo la ventana de impresion", "warning");
      return;
    }

    printWindow.document.write(`
      <!doctype html>
      <html>
        <head>
          <title>Pedido ${pedido.idPedido}</title>
          <style>
            body { font-family: Arial, Helvetica, sans-serif; font-size: 12px; color: #111; margin: 24px; }
            h1 { font-size: 18px; text-align: center; margin: 0 0 22px; }
            p { margin: 4px 0; }
            table { width: 100%; border-collapse: collapse; margin-top: 18px; }
            th, td { border: 1px solid #777; padding: 6px; vertical-align: top; }
            th { font-weight: 700; text-align: center; }
            .center { text-align: center; }
            .right { text-align: right; }
            .total td { border-left: 0; border-right: 0; border-bottom: 0; font-weight: 700; }
            @media print {
              body { margin: 12mm; }
            }
          </style>
        </head>
        <body>
          <h1>Pedido ${pedido.idPedido}</h1>
          <p>Fecha de Pedido: ${formatDate(pedido.fechaPedido)}</p>
          <p>Cliente: ${escapeHtml(pedido.cliente)}</p>
          <p>Telefono: ${escapeHtml(pedido.telefono)}</p>
          <p>Fecha de Entrega: ${formatDate(pedido.fechaEntrega)}</p>
          <p>Hora: ${escapeHtml(pedido.horaDesde)} a ${escapeHtml(pedido.horaHasta)}</p>
          ${pedido.observacion.trim() ? `<p>Obs: ${escapeHtml(pedido.observacion.trim())}</p>` : ""}
          ${pedido.infoEntrega.trim() ? `<p>Informacion de Entrega: ${escapeHtml(pedido.infoEntrega.trim())}</p>` : ""}
          <table>
            <thead>
              <tr>
                <th>Cantidad</th>
                <th>Descripcion</th>
                <th class="right">Total</th>
              </tr>
            </thead>
            <tbody>
              ${detalleHtml}
              <tr class="total">
                <td colspan="3" class="right">Total: $ ${formatNumber(totalPedido)}</td>
              </tr>
            </tbody>
          </table>
          <script>
            window.onload = function () {
              window.focus();
              window.print();
            };
          </script>
        </body>
      </html>
    `);
    printWindow.document.close();
  };

  const mapPedidoHistoricoParaImprimir = (pedido: PedidoConDetalleDto): PedidoParaImprimir => {
    const cabecera = pedido.clientePedido;
    const telefono = [cabecera.telefono, cabecera.celular].filter(Boolean).join(" - ");
    return {
      idPedido: cabecera.idClientePedido,
      idLocal: cabecera.idLocal,
      fechaPedido: new Date(cabecera.fecha),
      cliente: cabecera.cliente,
      telefono,
      fechaEntrega: cabecera.fechaEntrega,
      horaDesde: formatTime(cabecera.horaDesde),
      horaHasta: formatTime(cabecera.horaHasta),
      observacion: cabecera.observacion,
      infoEntrega: cabecera.retiraDelLocal ? "Retira del Local" : cabecera.sucursalEntrega,
      lineas: pedido.detalle.map((linea) => ({
        producto: {
          IDPRODUCTO: linea.idProducto,
          CODIGO: "",
          PRODUCTO: linea.producto,
          CONTADO: linea.unitario,
        },
        cantidad: linea.cantidad,
      })),
    };
  };

  const descargarPdfPedido = (pedido: PedidoParaImprimir | null = ultimoPedidoImpresion) => {
    const idLocalPedido = pedido?.idLocal ?? Number(idLocal);
    if (!userLoggedIn || !pedido || !idLocalPedido || Number.isNaN(idLocalPedido)) {
      showMessage("No hay un pedido guardado para descargar", "warning");
      return Promise.resolve();
    }

    setDescargandoPdf(true);
    return fetchDescargarPedidoPdf(userLoggedIn, idLocalPedido, pedido.idPedido)
      .catch((err) => {
        console.log(err);
        showMessage(err?.message ?? "No se pudo descargar el PDF del pedido", "error");
      })
      .finally(() => setDescargandoPdf(false));
  };

  const guardarPedido = () => {
    if (!userLoggedIn || !idLocal || Number.isNaN(Number(idLocal))) {
      showMessage("Debe seleccionar un local valido", "error");
      return;
    }

    if (!clienteSeleccionado) {
      showMessage("Debe seleccionar un cliente", "error");
      return;
    }

    const idCliente = getClienteId(clienteSeleccionado);
    const clienteNombre = getClienteNombre(clienteSeleccionado);
    if (!idCliente || Number.isNaN(idCliente)) {
      showMessage("No se pudo determinar el id del cliente seleccionado", "error");
      return;
    }

    if (lineas.length === 0 || lineas.some((linea) => linea.cantidad <= 0)) {
      showMessage("Debe cargar productos con cantidad mayor a cero", "error");
      return;
    }

    if (!fechaEntrega || !horaDesde || !horaHasta) {
      showMessage("Debe completar fecha y horario de entrega", "error");
      return;
    }

    if (!retiraDelLocal && !sucursalEntrega.trim()) {
      showMessage("Debe indicar la sucursal o domicilio de entrega", "error");
      return;
    }

    setGuardando(true);
    fetchCrearPedido(userLoggedIn, {
      idLocal: Number(idLocal),
      idCliente,
      cliente: clienteNombre,
      fechaEntrega,
      horaDesde,
      horaHasta,
      observacion,
      retiraDelLocal,
      sucursalEntrega,
    }, lineas)
      .then((res) => {
        const idPedido = res?.idPedido ?? res?.IdPedido;
        const pedidoParaImprimir: PedidoParaImprimir = {
          idPedido: Number(idPedido ?? 0),
          idLocal: Number(idLocal),
          fechaPedido: new Date(),
          cliente: clienteNombre,
          telefono: (clienteSeleccionado as any).TELEFONO ?? (clienteSeleccionado as any).Telefono ?? "",
          fechaEntrega,
          horaDesde,
          horaHasta,
          observacion,
          infoEntrega: retiraDelLocal ? "Retira del Local" : sucursalEntrega,
          lineas: lineas.map((linea) => ({ ...linea, producto: { ...linea.producto } })),
        };

        setUltimoPedidoImpresion(pedidoParaImprimir);
        showMessage(idPedido ? `Pedido ${idPedido} guardado correctamente` : "Pedido guardado correctamente", "success");
        descargarPdfPedido(pedidoParaImprimir);
        cargarUltimosPedidos(clienteSeleccionado);
        setLineas([]);
        setObservacion("");
        setSucursalEntrega("");
        setRetiraDelLocal(true);
      })
      .catch((err) => {
        console.log(err);
        showMessage(err?.message ?? "Error al guardar el pedido", "error");
      })
      .finally(() => setGuardando(false));
  };

  return (
    <Box sx={{ p: { xs: 1, sm: 3 } }}>
      <CatalogoVentaActualizadoDialog productos={productos} revision={revision} lineas={lineas} setLineas={setLineas} />
      <Typography variant="h4" gutterBottom>
        Pedido Web
      </Typography>

      <Grid container spacing={2} sx={{ mb: 3 }} alignItems="center">
        <Grid item xs={12} sm={4}>
          <Typography variant="h6">Local</Typography>
        </Grid>
        <Grid item xs={12} sm={4}>
          <LocalSelector onLocalChange={setIdLocal} />
        </Grid>
      </Grid>

      <Grid container spacing={2} sx={{ mb: 2 }}>
        <Grid item xs={12} sm={6}>
          <Button fullWidth variant="contained" startIcon={<PersonIcon />} onClick={openClienteDialog}>
            {clienteSeleccionado ? getClienteNombre(clienteSeleccionado) : "Seleccionar cliente"}
          </Button>
        </Grid>
        <Grid item xs={12} sm={6}>
          <Button
            fullWidth
            variant="contained"
            color="secondary"
            startIcon={loadingProductos ? <CircularProgress color="inherit" size={18} /> : <AddShoppingCartIcon />}
            onClick={openProductoDialog}
            disabled={!idLocal || loadingProductos}
          >
            {loadingProductos ? "Cargando productos..." : "Agregar producto"}
          </Button>
        </Grid>
      </Grid>

      {clienteSeleccionado && (
        <Box sx={{ mb: 3 }}>
          <Typography variant="h6" sx={{ mb: 1 }}>
            Ultimos 10 pedidos de {getClienteNombre(clienteSeleccionado)}
          </Typography>
          <TableContainer sx={{ border: "1px solid", borderColor: "divider", borderRadius: 1 }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Pedido</TableCell>
                  <TableCell>Fecha</TableCell>
                  <TableCell>Entrega</TableCell>
                  <TableCell align="right">Total</TableCell>
                  <TableCell align="center">Acciones</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {loadingUltimosPedidos ? (
                  <TableRow>
                    <TableCell colSpan={5} align="center">
                      <Box sx={{ py: 1, display: "flex", alignItems: "center", justifyContent: "center", gap: 1 }}>
                        <CircularProgress size={18} />
                        Cargando pedidos...
                      </Box>
                    </TableCell>
                  </TableRow>
                ) : ultimosPedidos.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={5} align="center">No hay pedidos anteriores para este cliente.</TableCell>
                  </TableRow>
                ) : ultimosPedidos.map((pedido) => (
                  <TableRow key={pedido.clientePedido.idClientePedido}>
                    <TableCell>{pedido.clientePedido.idClientePedido}</TableCell>
                    <TableCell>{formatDate(pedido.clientePedido.fecha)}</TableCell>
                    <TableCell>{formatDate(pedido.clientePedido.fechaEntrega)}</TableCell>
                    <TableCell align="right">{CurrencyFormatter.format(pedido.clientePedido.total)}</TableCell>
                    <TableCell align="center">
                      <Box sx={{ display: "flex", gap: 1, justifyContent: "center", flexWrap: "wrap" }}>
                        <Button
                          size="small"
                          variant="outlined"
                          startIcon={<PrintIcon />}
                          onClick={() => imprimirPedido(mapPedidoHistoricoParaImprimir(pedido))}
                        >
                          Imprimir
                        </Button>
                        <Button
                          size="small"
                          variant="outlined"
                          onClick={() => descargarPdfPedido(mapPedidoHistoricoParaImprimir(pedido))}
                          disabled={descargandoPdf}
                          startIcon={descargandoPdf ? <CircularProgress color="inherit" size={18} /> : undefined}
                        >
                          PDF
                        </Button>
                      </Box>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      )}

      <Grid container spacing={2} sx={{ mb: 2 }}>
        <Grid item xs={12} sm={4}>
          <TextField fullWidth type="date" label="Fecha de entrega" value={fechaEntrega} onChange={(e) => setFechaEntrega(e.target.value)} InputLabelProps={{ shrink: true }} />
        </Grid>
        <Grid item xs={6} sm={2}>
          <TextField fullWidth type="time" label="Desde" value={horaDesde} onChange={(e) => setHoraDesde(e.target.value)} InputLabelProps={{ shrink: true }} />
        </Grid>
        <Grid item xs={6} sm={2}>
          <TextField fullWidth type="time" label="Hasta" value={horaHasta} onChange={(e) => setHoraHasta(e.target.value)} InputLabelProps={{ shrink: true }} />
        </Grid>
        <Grid item xs={12} sm={4}>
          <FormControlLabel
            control={<Checkbox checked={retiraDelLocal} onChange={(e) => setRetiraDelLocal(e.target.checked)} />}
            label="Retira del local"
          />
        </Grid>
        {!retiraDelLocal && (
          <Grid item xs={12}>
            <TextField fullWidth label="Sucursal o domicilio de entrega" value={sucursalEntrega} onChange={(e) => setSucursalEntrega(e.target.value)} />
          </Grid>
        )}
        <Grid item xs={12}>
          <TextField fullWidth multiline minRows={2} label="Observacion" value={observacion} onChange={(e) => setObservacion(e.target.value)} />
        </Grid>
      </Grid>

      <Divider sx={{ mb: 2 }} />

      <TableContainer sx={{ border: "1px solid", borderColor: "divider", borderRadius: 1 }}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Producto</TableCell>
              <TableCell align="right">Unitario</TableCell>
              <TableCell align="right">Cantidad</TableCell>
              <TableCell align="right">Subtotal</TableCell>
              <TableCell align="center">Eliminar</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {lineas.length === 0 ? (
              <TableRow>
                <TableCell colSpan={5} align="center">No hay productos en el pedido.</TableCell>
              </TableRow>
            ) : lineas.map((linea, index) => (
              <TableRow key={`${linea.producto.IDPRODUCTO}-${index}`}>
                <TableCell>{linea.producto.PRODUCTO}</TableCell>
                <TableCell align="right">{CurrencyFormatter.format(getUnitario(linea))}</TableCell>
                <TableCell align="right" sx={{ width: 140 }}>
                  <TextField
                    type="number"
                    size="small"
                    value={linea.cantidad}
                    inputProps={{ min: 0, step: 1 }}
                    onChange={(e) => updateCantidad(index, e.target.value)}
                  />
                </TableCell>
                <TableCell align="right">{CurrencyFormatter.format(getUnitario(linea) * linea.cantidad)}</TableCell>
                <TableCell align="center">
                  <IconButton color="error" onClick={() => deleteLinea(index)}>
                    <DeleteIcon />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <Box sx={{ mt: 3, display: "flex", justifyContent: "space-between", alignItems: "center", gap: 2, flexWrap: "wrap" }}>
        <Typography variant="h6">Total: {CurrencyFormatter.format(total)}</Typography>
        <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", justifyContent: "flex-end" }}>
          <Button variant="outlined" startIcon={<PrintIcon />} onClick={() => imprimirPedido()} disabled={!ultimoPedidoImpresion}>
            Imprimir pedido
          </Button>
          <Button
            variant="outlined"
            onClick={() => descargarPdfPedido()}
            disabled={!ultimoPedidoImpresion || descargandoPdf}
            startIcon={descargandoPdf ? <CircularProgress color="inherit" size={18} /> : undefined}
          >
            {descargandoPdf ? "Descargando..." : "Descargar PDF"}
          </Button>
          <Button
            variant="contained"
            color="success"
            startIcon={guardando ? <CircularProgress color="inherit" size={18} /> : <SaveIcon />}
            onClick={guardarPedido}
            disabled={guardando}
          >
            {guardando ? "Guardando..." : "Guardar pedido"}
          </Button>
        </Box>
      </Box>

      <Dialog open={selectDialogOpen && seleccionandoCliente} fullWidth keepMounted onClose={() => setSelectDialogOpen(false)}>
        <Typography variant="h5" sx={{ p: 2 }}>
          Seleccionar cliente
        </Typography>
        <DynamicGrid
          data={dialogData}
          customHeaderMap={gridColumnDefinitions.clientes}
          pageSize={5}
          removeIfNotCustom={true}
          rowClickEventHandler={handleSelectRow}
        />
      </Dialog>
      <SelectorProductosVenta
        open={selectDialogOpen && seleccionandoProducto}
        productos={productos}
        lineas={lineas}
        onAgregar={handleAgregarProducto}
        onClose={() => setSelectDialogOpen(false)}
        destino="el pedido"
      />

    </Box>
  );
};

export default PedidoWeb;



